import type { ReactNode } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { ApiError } from '../api/client.ts';
import { Button } from './Button.tsx';

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <div role="status" className="flex items-center justify-center gap-2 py-10 text-mid">
      <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
      <span className="text-sm">{label}…</span>
    </div>
  );
}

export function ErrorState({ error, retry }: { error: unknown; retry?: () => void }) {
  const msg = error instanceof ApiError ? error.message : 'Something went wrong.';
  return (
    <div role="alert" className="flex flex-col items-center gap-3 rounded-2xl bg-bad-bg/40 px-4 py-8 text-center ring-1 ring-bad-fg/10">
      <AlertTriangle className="h-6 w-6 text-bad-fg" aria-hidden />
      <p className="text-ink">{msg}</p>
      {retry && (
        <Button variant="secondary" size="sm" onClick={retry}>
          Try again
        </Button>
      )}
    </div>
  );
}

export function Empty({ icon, title, children }: { icon?: ReactNode; title: string; children?: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-2 px-4 py-12 text-center">
      {icon && <div className="mb-1 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand/10 text-brand">{icon}</div>}
      <p className="font-semibold text-ink">{title}</p>
      {children && <div className="max-w-sm text-sm text-mid">{children}</div>}
    </div>
  );
}

/** Render a react-query result: spinner, error with retry, or the data. */
export function Load<T>({ q, children, label }: { q: { data: T | undefined; error: unknown; isLoading: boolean; refetch: () => unknown }; children: (data: T) => ReactNode; label?: string }) {
  if (q.isLoading) return <Spinner label={label} />;
  if (q.error) return <ErrorState error={q.error} retry={() => q.refetch()} />;
  if (q.data === undefined) return null;
  return <>{children(q.data)}</>;
}
